import React, { useState, useEffect, useContext } from "react";
import "popper.js";
import "bootstrap/dist/js/bootstrap";
import { useHistory } from "react-router-dom";
// Componentes
import Foto from "../element/Foto";
import HeaderMenu from "../element/HeaderMenu";
import Menu from "./Menu";
import Footer from "./Footer";
import SkeletonCard from "../element/Skeleton";
// Paneles del dashboard
import DashAvisos from "../element/dash/DashAvisos";
import DashTiles from "../element/dash/DashTiles";
import DashDestacados from "../element/dash/DashDestacados";
import DashEventos from "../element/dash/DashEventos";
import DashCorreo from "../element/dash/DashCorreo";
// Contexto
import dataContext from "../context/User";
// Helpers
import GetFetch from "../helper/GetFetch";
import GetFetchDTE from "../helper/GetFetchDTE";
import SetBitacora from "../helper/SetBitacora";
import ShortData from "../helper/ShortData";
import loadMenu from "../js/main";
import { parseFecha, fechaCorta } from "../element/dash/fechas";

// Normaliza la historia de DTE a { code, fecha } usando los campos que
// devuelve la API (la fecha de generación y el código vienen en variantes
// snake_case o PascalCase según el registro).
const normalizarDte = (lista) =>
    (Array.isArray(lista) ? lista : [])
        .map((el) => ({
            code: el && el.mh && (el.mh.codigo_generacion || el.mh.CodigoGeneracion),
            fecha: parseFecha(
                (el && el.fechageneracion) ||
                    (el && el.mh && (el.mh.fecha_procesado || el.mh.FechaProcesamiento || el.mh.Fecha))
            ),
        }))
        .filter((x) => x.code);

// Devuelve la factura más reciente por fecha de generación.
const facturaMasReciente = (items) =>
    items
        .slice()
        .sort((a, b) => (b.fecha ? b.fecha.getTime() : 0) - (a.fecha ? a.fecha.getTime() : 0))[0] || null;

const Dashboard = () => {
    const history = useHistory();
    const dataUser = useContext(dataContext);
    let datos = localStorage.getItem("dataUser");

    const [carrera, setCarrera] = useState("");
    const [saldos, setSaldos] = useState([]);
    const [planesPago, setPlanesPago] = useState([]);
    const [fechas, setFechas] = useState([]);
    const [prestamos, setPrestamos] = useState([]);
    const [cum, setCum] = useState([]);
    const [horasSociales, setHorasSociales] = useState([]);
    const [materias, setMaterias] = useState(0);
    const [pasantias, setPasantias] = useState(0);
    const [mostrarPasantias, setMostrarPasantias] = useState(false);
    const [eventos, setEventos] = useState([]);
    const [facturaNueva, setFacturaNueva] = useState(false);
    const [facturaFecha, setFacturaFecha] = useState("");
    const [facturasCount, setFacturasCount] = useState(0);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        if (!datos) {
            history.push("/");
            return;
        }

        const cred = JSON.parse(datos);
        dataUser.token = cred.token;
        dataUser.nombre = cred.nombre;
        dataUser.carnet = cred.carnet;
        dataUser.perfil = cred.perfil;
        dataUser.carrera = cred.carrera;
        setCarrera(cred.carrera);

        // Año de estudio a partir del carnet: los primeros 4 dígitos son el año
        // de ingreso (ej. 20225444 → ingreso 2022). Pasantías se muestra a
        // partir del 3.er año (ingreso ≤ año actual − 2).
        const anioIngreso = parseInt(String(cred.carnet || "").substring(0, 4), 10);
        const anioActual = new Date().getFullYear();
        const anioEstudio = anioActual - anioIngreso + 1;
        const verPasantias =
            anioIngreso >= 2000 && anioIngreso <= anioActual && anioEstudio >= 3;
        setMostrarPasantias(verPasantias);

        const seguro = (promesa) => promesa.then((d) => d).catch(() => ({}));

        Promise.all([
            seguro(GetFetch.saldosAlumno(cred.carnet, cred.token)),
            seguro(GetFetch.saldosPlanesPagoAlumno(cred.carnet, cred.token)),
            seguro(GetFetch.fechasVencimiento(cred.token)),
            seguro(GetFetch.prestamosActivos(cred.carnet, cred.token)),
            seguro(GetFetch.cum(cred.carnet, cred.token)),
            seguro(GetFetch.horasSociales(cred.carnet, cred.token)),
            seguro(GetFetch.horarios(cred.carnet, cred.token)),
            seguro(GetFetch.getEventos(cred.token)),
            seguro(GetFetchDTE.alumnoHistoria({ carnet: cred.carnet, secret: "5ec4702eff406849a82818cb" })),
            seguro(verPasantias ? GetFetch.getPasantiaAll(cred.token) : Promise.resolve([])),
        ]).then(([sal, pp, fec, pre, cumD, horas, hor, eve, dte, pas]) => {
            setSaldos(sal);
            setPlanesPago(pp);
            setFechas(fec);
            setPrestamos(pre);
            setCum(cumD);
            setHorasSociales(horas);
            // Materias inscritas = nº de materias distintas en el horario.
            setMaterias(Array.isArray(hor) ? ShortData.MateriaHoraios(hor).length : 0);
            setPasantias(Array.isArray(pas) ? pas.length : 0);
            setEventos(Array.isArray(eve) ? eve.slice().reverse() : []);

            // Facturas (DTE): conteo total y detección de "nueva" (la más
            // reciente cuyo código aún no ha sido visto). "Visto" se marca al
            // abrir /dtealumnos; NO se siembra en silencio aquí.
            const resultDte = dte && dte.success === 1 ? dte.result : [];
            const facturas = normalizarDte(resultDte);
            setFacturasCount(facturas.length);
            const reciente = facturaMasReciente(facturas);
            if (reciente) {
                const vistos = JSON.parse(localStorage.getItem("dteFacturasVistas_v2") || "[]");
                if (vistos.indexOf(reciente.code) === -1) {
                    setFacturaNueva(true);
                    setFacturaFecha(fechaCorta(reciente.fecha));
                }
            }

            setCargando(false);
        });

        loadMenu();
        SetBitacora.set(cred.carnet, "Dashboard", cred.token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datos, history]);

    const primerNombre = (dataUser.nombre || "").trim().split(" ")[0];

    return (
        <div className="wrapper d-flex align-items-stretch">
            <nav id="sidebar">
                <div className="custom-menu">
                    <button type="button" id="sidebarCollapse" className="btn btn-primary">
                        <i className="fa fa-bars"></i>
                        <span className="sr-only">Toggle Menu</span>
                    </button>
                </div>
                <div className="p-4">
                    <Foto foto={dataUser.carnet} alt="Foto alumno" tamanio="rounded" />

                    <HeaderMenu
                        carnet={dataUser.carnet}
                        nombre={dataUser.nombre}
                        perfil={dataUser.perfil}
                        carrera={dataUser.carrera}
                        posicion="text-center"
                    />

                    <Menu />

                    <Footer />
                </div>
            </nav>

            <div id="content" className="p-4 p-md-5 pt-5">
                <h2 className="mb-1 esen-anim-in esen-anim-in--1">
                    Hola{primerNombre ? `, ${primerNombre}` : ""} 👋
                </h2>
                <p className="text-muted mb-4 esen-anim-in esen-anim-in--2">
                    Este es el resumen de tus servicios en la ESEN.
                </p>

                {cargando ? (
                    <>
                        <SkeletonCard rows={2} title={false} />
                        <div className="esen-stat-grid mt-3">
                            <SkeletonCard rows={1} title={false} />
                            <SkeletonCard rows={1} title={false} />
                            <SkeletonCard rows={1} title={false} />
                            <SkeletonCard rows={1} title={false} />
                        </div>
                        <div className="esen-dash-grid mt-3">
                            <SkeletonCard rows={4} />
                            <SkeletonCard rows={4} />
                        </div>
                    </>
                ) : (
                    <>
                        <div className="esen-anim-in esen-anim-in--2">
                            <DashAvisos prestamos={prestamos} />
                        </div>

                        <h3 className="h5 mt-4 mb-3 esen-anim-in esen-anim-in--3">
                            Estado de tus servicios
                        </h3>
                        <div className="esen-anim-in esen-anim-in--3">
                            <DashTiles
                                saldos={saldos}
                                planesPago={planesPago}
                                prestamos={prestamos}
                                cum={cum}
                                horasSociales={horasSociales}
                                materias={materias}
                                pasantias={pasantias}
                                mostrarPasantias={mostrarPasantias}
                                facturasCount={facturasCount}
                                facturaNueva={facturaNueva}
                                facturaFecha={facturaFecha}
                            />
                        </div>

                        <div className="mt-4 esen-anim-in esen-anim-in--4">
                            <DashCorreo correoEsperado={`${dataUser.carnet}@esen.edu.sv`} />
                        </div>

                        <div className="esen-dash-grid mt-3 esen-anim-in esen-anim-in--4">
                            <DashDestacados carrera={carrera} />
                            <DashEventos eventos={eventos} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
